/**
 * Form validation utilities
 */

import { FormField, FieldValidation, ValidationErrors, FormTemplate } from '../types';

export class FormValidator {
  /**
   * Validate entire form data against template
   */
  static validateForm(data: Record<string, any>, template: FormTemplate): ValidationErrors {
    const errors: ValidationErrors = {};

    for (const field of template.fields) {
      // Skip non-input fields
      if (['divider', 'heading', 'paragraph', 'hidden'].includes(field.type)) {
        continue;
      }

      const value = data[field.name];
      const error = this.validateField(field, value);
      
      if (error) {
        errors[field.name] = error;
      }
    }

    return errors;
  }

  /**
   * Validate single field
   */
  static validateField(field: FormField, value: any): string | null {
    if (!field.validation) {
      return null;
    }

    return this.validateValue(field.name, value, field.validation, field.type);
  }

  /**
   * Validate value against validation rules
   */
  static validateValue(
    fieldName: string, 
    value: any, 
    validation: FieldValidation,
    fieldType?: string
  ): string | null {
    // Required validation
    if (validation.required && this.isEmpty(value)) {
      return validation.customMessage || `${fieldName} is required`;
    }

    // Skip other validations for empty optional fields
    if (this.isEmpty(value)) {
      return null;
    }

    // String validations
    if (typeof value === 'string') {
      // Length validations
      if (validation.minLength && value.length < validation.minLength) {
        return validation.customMessage || `Must be at least ${validation.minLength} characters`;
      }
      
      if (validation.maxLength && value.length > validation.maxLength) {
        return validation.customMessage || `Must be no more than ${validation.maxLength} characters`;
      }
      
      // Pattern validation
      if (validation.pattern && !new RegExp(validation.pattern).test(value)) {
        return validation.customMessage || 'Invalid format';
      }
      
      // Email validation
      if (fieldType === 'email' && !this.isValidEmail(value)) {
        return validation.customMessage || 'Please enter a valid email address';
      }
      
      // URL validation
      if (fieldType === 'url' && !this.isValidUrl(value)) {
        return validation.customMessage || 'Please enter a valid URL';
      }
      
      // Phone validation
      if (fieldType === 'tel' && !this.isValidPhone(value)) {
        return validation.customMessage || 'Please enter a valid phone number';
      }
    }

    // Number validations
    if (fieldType === 'number' && value !== '') {
      const numValue = parseFloat(value);
      
      if (isNaN(numValue)) {
        return validation.customMessage || 'Please enter a valid number';
      }
      
      if (validation.min !== undefined && numValue < validation.min) {
        return validation.customMessage || `Must be at least ${validation.min}`;
      }
      
      if (validation.max !== undefined && numValue > validation.max) {
        return validation.customMessage || `Must be no more than ${validation.max}`;
      }
    }

    // Date validations
    if (['date', 'datetime-local', 'time'].includes(fieldType || '')) {
      if (!this.isValidDate(value)) {
        return validation.customMessage || 'Please enter a valid date';
      }
    }

    // File validations
    if (fieldType === 'file' && value instanceof FileList) {
      const fileError = this.validateFiles(value, validation);
      if (fileError) {
        return validation.customMessage || fileError;
      }
    }

    return null;
  }

  /**
   * Check if value is empty
   */
  private static isEmpty(value: any): boolean {
    if (value === null || value === undefined) {
      return true;
    }
    
    if (typeof value === 'string') {
      return value.trim().length === 0;
    }
    
    if (Array.isArray(value)) {
      return value.length === 0;
    }
    
    if (value instanceof FileList) {
      return value.length === 0;
    }
    
    return false;
  }

  /**
   * Email validation
   */
  private static isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * URL validation
   */
  private static isValidUrl(url: string): boolean {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Phone number validation (basic)
   */
  private static isValidPhone(phone: string): boolean {
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
    const cleanPhone = phone.replace(/[\s\-\(\)\.]/g, '');
    return phoneRegex.test(cleanPhone);
  }

  /**
   * Date validation
   */
  private static isValidDate(date: string): boolean {
    if (!date) return false;
    const parsedDate = new Date(date);
    return !isNaN(parsedDate.getTime());
  }

  /**
   * File validation
   */
  private static validateFiles(files: FileList, validation: FieldValidation): string | null {
    if (files.length === 0) {
      return null;
    }

    // File count validation
    if (validation.max && files.length > validation.max) {
      return `Too many files. Maximum ${validation.max} files allowed`;
    }

    // Individual file validation
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      // File size validation (assuming min/max are in bytes for files)
      if (validation.min && file.size < validation.min) {
        return `File "${file.name}" is too small. Minimum size: ${this.formatFileSize(validation.min)}`;
      }
      
      if (validation.max && file.size > validation.max) {
        return `File "${file.name}" is too large. Maximum size: ${this.formatFileSize(validation.max)}`;
      }
    }

    return null;
  }

  /**
   * Format file size for display
   */
  private static formatFileSize(bytes: number): string {
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  }
}

/**
 * Real-time validation hook utility
 */
export class RealtimeValidator {
  private validators: Map<string, (value: any) => Promise<string | null>> = new Map();
  private debounceTimers: Map<string, NodeJS.Timeout> = new Map();

  /**
   * Register field validator
   */
  registerField(fieldName: string, validator: (value: any) => Promise<string | null>): void {
    this.validators.set(fieldName, validator);
  }

  /**
   * Validate field with debouncing
   */
  async validateField(
    fieldName: string, 
    value: any, 
    onResult: (error: string | null) => void,
    debounceMs: number = 300
  ): Promise<void> {
    // Clear existing timer
    const existingTimer = this.debounceTimers.get(fieldName);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    // Set new timer
    const timer = setTimeout(async () => {
      const validator = this.validators.get(fieldName);
      if (validator) {
        try {
          const error = await validator(value);
          onResult(error);
        } catch (err) {
          console.warn(`Validation error for field ${fieldName}:`, err);
          onResult(null); // Don't show errors for validation failures
        }
      }
      this.debounceTimers.delete(fieldName);
    }, debounceMs);

    this.debounceTimers.set(fieldName, timer);
  }

  /**
   * Clear all timers
   */
  destroy(): void {
    this.debounceTimers.forEach(timer => clearTimeout(timer));
    this.debounceTimers.clear();
    this.validators.clear();
  }
}

export default FormValidator;